import { runCommand } from './shell/index.js';

export function isInsideGitRepo(cwd: string) {
  return runCommand('git rev-parse --is-inside-work-tree', { cwd })
    .then(() => true)
    .catch(() => false);
}

export async function getGitRoot(cwd: string) {
  return runCommand('git rev-parse --show-toplevel', { cwd });
}

export async function getGitLatestCommitHash(cwd: string) {
  try {
    return await runCommand('git rev-parse --verify HEAD', { cwd });
  } catch {
    // This command can fail if there are no commits, so we can just return undefined
  }
}

export async function gitInit(cwd: string, branch = 'main') {
  await runCommand(`git init -b ${branch.trim()}`, { cwd });
}

export async function gitCommit(cwd: string) {
  await runCommand('git add -A', { cwd });
  await runCommand(
    `git commit -m Initial\\ commit\\ from\\ Create\\ Wix\\ App --no-verify`,
    { cwd }
  );
}

export async function gitClone(url: string, path: string, branch?: string) {
  await runCommand(
    `git clone --depth 1 ${branch ? `-b ${branch}` : ''} ${url} ${path}`,
    {
      env: {
        /*
         * This is to prevent git from prompting for credentials.
         * `git clone` uses a subprocess for the authentication, so we can't attach the main process to its stdio.
         * This leads to:
         *  - the spinners "swallow" its output.
         *  - after answering with the username and pressing "Enter", the first line of the output is being duplicated.
         * That's why we're disabling the prompt, so user should be pre-authenticated.
         */
        GIT_TERMINAL_PROMPT: '0',
      },
    }
  );
}

export async function gitGetCurrentBranch(cwd: string) {
  return runCommand('git rev-parse --abbrev-ref HEAD', { cwd });
}

async function gitHasRemote(cwd: string, remoteName: string) {
  try {
    await runCommand(`git remote get-url ${remoteName}`, { cwd });
    return true;
  } catch {
    return false;
  }
}

export async function gitSetRemoteUrl(
  cwd: string,
  remoteName = 'origin',
  remoteUrl: string
) {
  const hasRemote = await gitHasRemote(cwd, remoteName);
  if (hasRemote) {
    await runCommand(`git remote set-url ${remoteName} ${remoteUrl}`, { cwd });
  } else {
    await runCommand(`git remote add ${remoteName} ${remoteUrl}`, { cwd });
  }
}

export async function gitPush(
  cwd: string,
  remoteName = 'origin',
  options: { force?: boolean } = {}
) {
  const branch = await gitGetCurrentBranch(cwd);
  const forceFlag = options.force ? '--force' : '';
  await runCommand(`git push -u ${forceFlag} ${remoteName} ${branch}`, {
    cwd,
    env: {
      // Prevent git from prompting for credentials
      GIT_TERMINAL_PROMPT: '0',
    },
  });
}

export async function gitGetUserInfo(cwd: string) {
  const [name, email] = await Promise.all([
    runCommand('git config user.name', { cwd }),
    runCommand('git config user.email', { cwd }),
  ]);

  return { name, email };
}
